import { DynamicTable } from "@/components/DynamicTable";
import { WithAuth } from "@/hoc/WithAuth";
import { FillLockIcon } from "@/styles/icons";
import { UnlockIcon } from "@/styles/icons";
import { formatCardType, formatCurrency, formatCurrencyToApi, formatFinalNumbers, showToast } from "@/utils";
import { Flex, Box, Text, Heading, Button, Badge, Skeleton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {  InputCurrencyBRL } from "@/components";
import { useCreditCard } from "@/hooks/useCreditCard";

interface ListCreditCardProps {
  cardId: string;
  panMasked: string;
  cardName: string;
  cardStatus: string;
  type: string;
  invoiceValue: number;
  limitValue: number;
}

const ManageCreditCardLimitsPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    initialDataIsLoading,
    creditCards,
    getInitialData,
    creditCardLimit,
    changeCreditCardLimit,
    originalLimits,
    editedLimits,
    setEditedLimits,
  } = useCreditCard();

  const totalFixedLimit = creditCards.reduce((acc, card) => {
    if (card.type === "LIMIT_BALANCE") {
      return acc + card.limitValue;
    }
    return acc;
  }, 0);

  useEffect(() => {
    if (creditCards.length === 0) {
      getInitialData();
    }
  }, [creditCards]);

  function formatCardStatusIcon(status: string) {
    const cardStatusIcon = {
      ACTIVE: <UnlockIcon size={14} />,
      BLOCKED: <FillLockIcon size={14} />,
    }
    return cardStatusIcon[status] || <FillLockIcon size={14} />;
  }
  const handleEditClick = async () => {
    if (isEditing) {
      const changedCards = creditCards.reduce((acc, card, idx) => {
        if (card.type !== "FREE_BALANCE" && editedLimits[idx] !== originalLimits[idx]) {
          acc.push({
            cardId: card.cardId,
            newLimit: editedLimits[idx]
          });
        }
        return acc;
      }, []);

      if (changedCards.length > 0) {
        changedCards.map(async (card) => {
          try {
            await changeCreditCardLimit(card);
            showToast({
              type: "success",
              message: "Limites atualizados com sucesso.",
            });
          } catch (error) {
            error?.response?.data?.errors.map((error) => {
              showToast({
                type: "error",
                message: `${error?.code} - ${error?.description}`,
              });
            });
            const cardIndex = creditCards.findIndex(c => c.cardId === card.cardId);
            if (cardIndex !== -1) {
              setEditedLimits(prev => prev.map((limit, idx) => 
                idx === cardIndex ? originalLimits[cardIndex] : limit
              ));
            }
          }
        });
        getInitialData();
      }
    }
    setIsEditing(!isEditing);
  };
  const handleLimitChange = (idx: number, value: string) => {
    setEditedLimits(limits => limits.map((lim: number, i: number) => i === idx ? formatCurrencyToApi(value) : lim));
  };

  const columns = [
    {
      key: "panMasked",
      title: "Cartão",
      dataIndex: "panMasked",
      sorter: false,
      render: (panMasked) => (
        <Flex align="center" gridGap="2">
          <Text>{`final `}</Text>
          <Text
            fontWeight={600}
          >
            {formatFinalNumbers(panMasked)}
          </Text>
        </Flex>
      ),
    },
    {
      key: "cardName",
      title: "Nome",
      dataIndex: "cardName",
      sorter: false,
      render: (cardName, row) => (
        <Flex align="center" gridGap="2">
          {formatCardStatusIcon(row.cardStatus)}
          <Text>{cardName}</Text>
        </Flex>
      ),
    },
    {
      key: "",
      title: "",
      dataIndex: "",
      sorter: false,
      emptyText: true,
    },
    {
      key: "type",
      title: "",
      dataIndex: "type",
      sorter: false,
      emptyText: true,
      render: (type: string) => (
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          fontWeight={400}
          fontSize="sm"
          color="gray.700"
          bg="gray.25"
        >
          {formatCardType(type)}
        </Badge>
      ),
    },
    {
      key: "invoiceValue",
      title: "Fatura atual",
      dataIndex: "invoiceValue",
      sorter: false,
      render: (invoiceValue) => (
        <Text
          fontWeight={400}
          fontSize="12px"
        >
          {formatCurrency({ value: invoiceValue })}
        </Text>
      ),
    },
    {
      key: "limite",
      title: "Limite",
      dataIndex: "limite",
      sorter: false,
      render: (limitValue, row, rowIndex) => {
        const isSaldoLivre = row?.type?.toLowerCase() === "FREE_BALANCE" || row?.type === "FREE_BALANCE";
        return (
          <InputCurrencyBRL
            value={editedLimits[rowIndex]}
            onChangeInput={e => handleLimitChange(rowIndex, e)}
            fontSize="12px"
            borderRadius="8px"
            isLimit
            _focus={{
              borderColor: "black",
            }}
            isDisabled={!isEditing || isSaldoLivre}
          />
        );
      },
    },
    {
      key: "actions",
      title: "",
      dataIndex: "actions",
      sorter: false,
      emptyText: true,
      rowAction: (row, rowIndex) => (
        <Flex gap={2}>
          <Button
            size="sm"
            colorScheme="blue"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              console.log('Ver detalhes do cartão:', row);
            }}
          >
            Ver
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              console.log('Bloquear cartão:', row);
            }}
          >
            Bloquear
          </Button>
        </Flex>
      ),
    },
  ];
  return (
    <Flex flexDir="column" gap={4}>
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center" gap={2}>
          <Heading fontSize="1.5rem">Gerenciar limites</Heading>
        </Flex>
        <Flex gap={2} align="center">
          {isEditing && (
            <Button
              variant="ghost2"
              borderRadius="8px"
              py="8px"
              px="24px"
              fontWeight={400}
              onClick={() => setIsEditing(false)}
              color="#000"
              _hover={{
                background: "#000",
                color: "#fff",
              }}
              size="sm"
            >
              <Text>Cancelar</Text>
            </Button>
          )}
          <Button
            variant="ghost2"
            borderRadius="8px"
            py="8px"
            px="24px"
            fontWeight={400}
            onClick={handleEditClick}
            color="#000"
            _hover={{
              background: "#000",
              color: "#fff",
            }}
            size="sm"
          >
            <Text>{isEditing ? "Salvar" : "Editar"}</Text>
          </Button>
        </Flex>
      </Flex>
      <Flex align="flex-end" justify="space-between" mb={6}>
        <Box>
          <Text color="#A3A3A3" fontSize="sm">Saldos Fixos</Text>
          <Skeleton isLoaded={!initialDataIsLoading}>
            <Text fontWeight={600} fontSize="lg">{formatCurrency({ value: totalFixedLimit })}</Text>
          </Skeleton>
        </Box>
        <Box textAlign="right">
          <Text color="#A3A3A3" fontSize="sm">Limite global</Text>
          <Skeleton isLoaded={!initialDataIsLoading}>
            <Text fontWeight={700} fontSize="2xl">{formatCurrency({ value: creditCardLimit?.totalValueBag })}</Text>
          </Skeleton>
            <Text color="#A3A3A3" fontSize="sm">Limite restante 
              <Skeleton isLoaded={!initialDataIsLoading}><b>{formatCurrency({ value: creditCardLimit?.availableValueVirtualCard })}</b></Skeleton>
            </Text>
        </Box>
      </Flex>
      <DynamicTable
        data={creditCards}
        columns={columns}
        loading={initialDataIsLoading}
      />
    </Flex>
  );
}

export default WithAuth(ManageCreditCardLimitsPage);